import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useV3Api } from '../api/useV3Api';
import { errorText } from '../api/errorText';
import ColumnHeads from '../components/drift/ColumnHeads';
import DeviceView from '../components/drift/DeviceView';
import PageHeader from '../components/PageHeader';
import ShowCommand from '../components/ShowCommand';
import { SEVERITIES, bySeverity, findingsOf, severityClass } from '../drift/findingModel';
import { useTableView } from '../drift/useTableView';
import { useV3Account } from '../useV3Account';

const ROUTE = 'v3/devices';
const SPAN = 6;                                    // the caret and the five columns
const MAX_TAGS = 4;

const isOpenDevice = (d, open) => !!open && [d.hostname, d.deviceId].some((v) => String(v || '').toLowerCase() === open.toLowerCase());

// The machines of a customer as VES lists them, with the labels that decide which policies apply and the findings open on each. A row opens
// in place to its labels, its findings and (on request) its facts. ?device= in the address opens that machine (the findings page links here).
export default function DevicesPage() {
  const { get } = useV3Api();
  const { accounts, account, choose, loading: accountsLoading, error: accountsError } = useV3Account();
  const [params] = useSearchParams();
  const [list, setList] = useState({ loading: true, items: [], error: '' });
  const [findings, setFindings] = useState({ items: [], error: '' });
  const [text, setText] = useState('');
  const [open, setOpen] = useState(params.get('device') || '');       // a hostname, '' for none
  const code = account && account.code;

  useEffect(() => {
    if (!code) return undefined;
    let cancelled = false;
    setList((l) => ({ ...l, loading: true }));
    get(ROUTE, { account: code })
      .then((body) => { if (!cancelled) setList({ loading: false, items: (body && body.items) || [], error: '' }); })
      .catch((ex) => { if (!cancelled) setList({ loading: false, items: [], error: errorText(ex) }); });
    // The findings are a second, separate answer: a problem with them leaves the machines listed.
    get('v3/findings', { account: code })
      .then((body) => { if (!cancelled) setFindings({ items: (body && body.items) || [], error: '' }); })
      .catch((ex) => { if (!cancelled) setFindings({ items: [], error: errorText(ex) }); });
    return () => { cancelled = true; };
  }, [get, code]);

  // The columns need the findings (the last one counts them), so they are made again when the findings are read.
  const columns = useMemo(() => {
    const mine = (d) => findingsOf(findings.items, d);
    const weight = (d) => mine(d).reduce((n, f) => n + [1e6, 1e4, 100, 1, 0.01][SEVERITIES.indexOf(f.severity)], 0);
    return [
      { key: 'device', label: 'Device', text: (d) => d.hostname },
      { key: 'type', label: 'Type', text: (d) => (d.labels || {}).devicetype || '', options: true },
      { key: 'labels', label: 'Labels', text: (d) => Object.keys(d.labels || {}).filter((l) => l !== 'account').sort().map((l) => `${l} is ${d.labels[l]}`).join(', ') },
      { key: 'connection', label: 'Connection', text: (d) => d.connectionState || '', options: true },
      { key: 'open', label: 'Open findings', text: (d) => bySeverity(mine(d)).map((s) => `${s.count} ${s.severity}`).join(', '), sort: weight },
    ];
  }, [findings.items]);
  const base = useMemo(() => list.items.filter((d) => {
    const wanted = text.trim().toLowerCase();
    return !wanted || `${d.hostname} ${d.deviceId} ${Object.values(d.labels || {}).join(' ')}`.toLowerCase().includes(wanted);
  }), [list.items, text]);
  const view = useTableView(base, columns);
  const rows = open ? base.filter((d) => isOpenDevice(d, open)) : view.rows;       // an open machine stays on screen whatever the column filters say

  const changeAccount = (value) => { choose(value); setOpen(''); view.clear(); };

  return (
    <>
      <PageHeader
        title="Devices"
        bear="gomi"
        subtitle="The machines VES lists for a customer, with the labels that decide which policies apply. Click a machine to see its findings and facts."
        actions={<ShowCommand route={ROUTE} params={{ account: code }} disabled={!code} />}
      />

      {accountsError && <div className="field-error" role="alert">{accountsError}</div>}
      {findings.error && <div className="field-error" role="alert">Findings are not available: {findings.error}</div>}

      <div className="card">
        <div className="filter-bar toolbar">
          <div className="field inline">
            <label htmlFor="v3-account">Account</label>
            <select id="v3-account" value={code || ''} disabled={accountsLoading || accounts.length === 0} onChange={(e) => changeAccount(e.target.value)}>
              {accounts.map((a) => <option key={a.code} value={a.code}>{`${a.code} - ${a.name}`}</option>)}
            </select>
          </div>
          <div className="field inline">
            <label htmlFor="f-text">Search</label>
            <input id="f-text" type="search" value={text} placeholder="name or label" onChange={(e) => { setText(e.target.value); setOpen(''); }} />
          </div>
        </div>

        {list.loading && <div className="empty">Loading...</div>}
        {list.error && <div className="card-body"><div className="field-error" role="alert">{list.error}</div></div>}
        {!list.loading && !list.error && base.length === 0 && <div className="empty">{list.items.length === 0 ? 'VES lists no machines for this account.' : 'No machines match.'}</div>}
        {base.length > 0 && (
          <div className={`table-wrap${open ? ' has-open' : ''}`}>
            <table className="open-rows" aria-label="Devices">
              <thead>
                <ColumnHeads view={view} lead={1} />
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={SPAN} className="empty">No machines match the column filters. <button type="button" className="btn btn-sm" onClick={view.clear}>Clear filters</button></td></tr>
                )}
                {rows.map((d) => {
                  const mine = findingsOf(findings.items, d);
                  const isOpen = isOpenDevice(d, open);
                  const labels = Object.keys(d.labels || {}).filter((l) => l !== 'account').sort();
                  const toggle = () => setOpen(isOpen ? '' : d.hostname);
                  return (
                    <React.Fragment key={d.deviceId}>
                      <tr className={`row${isOpen ? ' open' : ''}`} data-testid={`device-${d.deviceId}`} aria-expanded={isOpen} tabIndex={0} onClick={toggle}
                        onKeyDown={(e) => { if ((e.key === 'Enter' || e.key === ' ') && e.target === e.currentTarget) { e.preventDefault(); toggle(); } }}>
                        <td><span className="caret" aria-hidden="true">&#9656;</span></td>
                        <td className="strong">{d.hostname}<div className="sub">{d.deviceId}</div></td>
                        <td>{(d.labels || {}).devicetype || '-'}</td>
                        <td className="contents">
                          {labels.slice(0, MAX_TAGS).map((l) => <span className="tag" key={l}>{`${l} is ${d.labels[l]}`}</span>)}
                          {labels.length > MAX_TAGS && <span className="tag">+{labels.length - MAX_TAGS} more</span>}
                        </td>
                        <td>{d.connectionState || '-'}</td>
                        <td>
                          {mine.length === 0 ? <span className="sub">none</span>
                            : bySeverity(mine).map((s) => <span key={s.severity} className={`pill pill-count ${severityClass(s.severity)}`}>{s.count} {s.severity}</span>)}
                        </td>
                      </tr>
                      {isOpen && (
                        <tr className="detail" data-testid={`detail-${d.deviceId}`}>
                          <td colSpan={SPAN}><DeviceView key={d.deviceId} device={d} account={code} findings={mine} /></td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </>
  );
}
